import { type Action, isRejectedWithValue } from "@reduxjs/toolkit";
import type { Middleware } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";

import { setError } from "../slices/errorSlice";
import { stopSilentMode } from "@store/slices/dashboardSlice";

type ApiFieldError = {
    field?: string[] | null;
    message?: string;
    detail?: string;
    non_field_errors?: string | string[];
};

type ApiErrorPayload = {
    data?: {
        errors?: ApiFieldError[];
        message?: string;
        error?: string;
        response?: string;
        non_field_errors?: string[];
        detail?: string;
        error_message?: string;
        is_active?: boolean;
    };
    error?: string;
    status?: number;
};

interface RejectedAction extends Action {
    payload: ApiErrorPayload;
    meta?: {
        arg?: {
            endpointName?: string;
            originalArgs?: unknown;
        };
        requestId?: string;
    };
    error?: unknown;
}

function buildUserMessage(payload?: ApiErrorPayload): string {
    const fallback = "Something went wrong";
    if (!payload) return fallback;

    const errors = payload.data?.errors;

    if (Array.isArray(errors) && errors.length > 0) {
        const msg = errors
            .flatMap((e) => {
                if (e.detail) return e.detail;
                if (e.message) return e.message;

                if (e.non_field_errors) {
                    return Array.isArray(e.non_field_errors)
                        ? e.non_field_errors
                        : [e.non_field_errors];
                }

                return Object.entries(e)
                    .filter(
                        ([key]) =>
                            !["field", "detail", "message", "non_field_errors"].includes(key)
                    )
                    .map(([key, value]) =>
                        Array.isArray(value)
                            ? `${key}: ${value.join(", ")}`
                            : `${key}: ${value}`
                    );
            })
            .filter(Boolean)
            .join(", ");

        return msg || fallback;
    }

    return (
        payload.data?.message ||
        payload.data?.error ||
        payload.error ||
        payload.data?.response ||
        payload.data?.detail ||
        payload.data?.non_field_errors?.[0] ||
        fallback
    );
}

function toSentryError(message: string, payload?: ApiErrorPayload): Error {
    const err = new Error(message);
    err.name = "RTKQueryError";

    try {
        (err as any).cause = payload;
    } catch {
        // ignore
    }

    return err;
}

export const rtkQueryErrorLogger: Middleware =
    ({ dispatch }) =>
        (next) =>
            (action) => {
                if (!isRejectedWithValue(action)) {
                    return next(action);
                }

                const rejectedAction = action as RejectedAction;
                const payload = rejectedAction.payload;
                const status = payload?.status;
                const endpointName = rejectedAction.meta?.arg?.endpointName ?? "unknown_endpoint";

                dispatch(stopSilentMode());

                if (status === 401) {
                    return next(action);
                }

                if (status === 503 && payload?.data?.error === "TS_UNAVAILABLE") {
                    console.warn(
                        "ThoughtSpot cluster is unreachable. Suppressing global error."
                    );
                    return next(action);
                }

                if (status === 503 && !payload?.data?.is_active) {
                    return next(action);
                }

                if (payload?.data?.detail === "No active subscription") {
                    console.warn("User has no active subscription. Suppressing toaster.");
                    return next(action);
                }

                const message = buildUserMessage(payload);

                Sentry.withScope((scope) => {
                    scope.setTag("layer", "rtk-query");
                    scope.setTag("endpoint", endpointName);

                    if (typeof status === "number") {
                        scope.setTag("http.status_code", String(status));
                    }

                    scope.setContext("rtkQuery", {
                        endpointName,
                        status,
                        requestId: rejectedAction.meta?.requestId,
                        originalArgs: rejectedAction.meta?.arg?.originalArgs,
                    });

                    scope.setExtra("payload", payload);
                    scope.setExtra("rejectedAction", {
                        type: rejectedAction.type,
                        meta: rejectedAction.meta,
                        error: rejectedAction.error,
                    });

                    Sentry.captureException(
                        toSentryError(message, payload)
                    );
                });

                dispatch(
                    setError({
                        message,
                        type: "error",
                    })
                );

                return next(action);
            };
